import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { buildAuthOverlayUrl } from "../../utils/authNavigation";
import HeaderProfileMenu from "../shared/HeaderProfileMenu";
import { getProfilePathForRole } from "../../utils/profileMenuPaths";
import nemaCareLogo from "../../assets/design/nema-care-logo.png";
import "./PublicHeader.css";

export default function PublicHeader({ active = "find", showUtilities = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const token = api.getToken?.();
  const isGuest = !token;
  const authRole = isGuest ? "" : api.getAuthRoleFromToken?.() || "";

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  function scrollTo(id) {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    navigate(`/#${id}`);
  }

  function openSignIn() {
    setMenuOpen(false);
    navigate(
      buildAuthOverlayUrl(location.pathname || "/", {
        returnTo: "/marketplace",
        tab: "login",
      })
    );
  }

  function openCreateAccount() {
    setMenuOpen(false);
    navigate(
      buildAuthOverlayUrl(location.pathname || "/", {
        returnTo: "/marketplace",
        tab: "create",
      })
    );
  }

  function openPartnerSignup(role) {
    setMenuOpen(false);
    if (role === "caregiver") {
      navigate("/become-caregiver");
      return;
    }
    navigate(
      buildAuthOverlayUrl(location.pathname || "/", {
        tab: "create",
        role,
        returnTo: "/care-manager",
      })
    );
  }

  return (
    <header className="public-header">
      <div className="public-header__inner">
        <Link to="/" className="public-header__brand" aria-label="NEMA Care Hub">
          <img src={nemaCareLogo} alt="NEMA Care Hub" className="public-header__logo-img" />
        </Link>

        <nav className="public-header__nav" aria-label="Main">
          <button
            type="button"
            className={`public-header__nav-link public-header__nav-link--find${active === "find" ? " public-header__nav-link--active" : ""}`}
            onClick={() => scrollTo("caregivers")}
          >
            Find Caregivers
          </button>
          <div className="public-header__partner-links">
            <button
              type="button"
              className={`public-header__nav-link public-header__nav-link--icon${active === "caregiver" ? " public-header__nav-link--active" : ""}`}
              onClick={() => openPartnerSignup("caregiver")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              Become a Caregiver
            </button>
            <button
              type="button"
              className="public-header__nav-link public-header__nav-link--icon"
              onClick={() => openPartnerSignup("manager")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
                <rect x="8" y="2" width="8" height="4" rx="1" />
                <path d="M9 14l2 2 4-4" />
              </svg>
              Become a Care Manager
            </button>
          </div>
          <Link
            to="/pricing"
            className={`public-header__nav-link public-header__nav-link--icon${active === "pricing" ? " public-header__nav-link--active" : ""}`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <path d="M2 10h20" />
            </svg>
            Pricing &amp; Plans
          </Link>
        </nav>

        <div className="public-header__actions">
          <button
            type="button"
            className="public-header__menu-btn"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>

          {showUtilities && !isGuest ? (
            <>
              <button type="button" className="public-header__icon-btn" aria-label="Messenger">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
              </button>
              <button type="button" className="public-header__icon-btn public-header__icon-btn--bell" aria-label="Notifications">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 01-3.46 0" />
                </svg>
                <span className="public-header__badge">2</span>
              </button>
              <button type="button" className="public-header__avatar" onClick={() => navigate("/marketplace")} aria-label="My account">
                <span>U</span>
              </button>
            </>
          ) : isGuest ? (
            <div className="public-header__guest-cluster">
              <button type="button" className="public-header__guest" onClick={openCreateAccount}>
                <span className="public-header__guest-avatar" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                Welcome Guest
                <span className="public-header__caret">▾</span>
              </button>
              <button type="button" className="public-header__sign-in" onClick={openSignIn}>
                Sign In
              </button>
            </div>
          ) : (
            <HeaderProfileMenu
              displayName={authRole === "CAREGIVER" ? "Caregiver" : "My account"}
              roleLabel=""
              initials={authRole?.charAt(0) || "U"}
              authRole={authRole}
              profilePath={getProfilePathForRole(authRole)}
            />
          )}
        </div>
      </div>

      {menuOpen ? (
        <>
          <button type="button" className="public-header__menu-overlay" onClick={() => setMenuOpen(false)} aria-label="Close menu" />
          <nav className="public-header__mobile-nav" aria-label="Mobile navigation">
            <button type="button" className="public-header__mobile-link" onClick={() => scrollTo("caregivers")}>
              Find Caregivers
            </button>
            <button
              type="button"
              className="public-header__mobile-link"
              onClick={() => openPartnerSignup("caregiver")}
            >
              Become a Caregiver
            </button>
            <button
              type="button"
              className="public-header__mobile-link"
              onClick={() => openPartnerSignup("manager")}
            >
              Become a Care Manager
            </button>
            <Link to="/pricing" className="public-header__mobile-link" onClick={() => setMenuOpen(false)}>
              Pricing &amp; Plans
            </Link>
            {isGuest ? (
              <button type="button" className="public-header__mobile-link public-header__mobile-link--cta" onClick={openSignIn}>
                Sign in
              </button>
            ) : (
              <button
                type="button"
                className="public-header__mobile-link public-header__mobile-link--cta"
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/marketplace");
                }}
              >
                My Account
              </button>
            )}
          </nav>
        </>
      ) : null}
    </header>
  );
}
